import {
  build_question_option,
  build_question_option_label,
  build_system_field_label,
} from "./formal_document_template_helpers";

export const FORMAL_TEMPLATE_WIZARD_STEPS = [
  {
    key: "basics",
    title: "Basics",
    summary: "Set the PDF type and template details.",
  },
  {
    key: "scope",
    title: "Scope",
    summary: "Choose where this PDF can be used.",
  },
  {
    key: "branding",
    title: "Branding",
    summary: "Add the logo, header, and footer.",
  },
  {
    key: "content",
    title: "Content",
    summary: "Build the PDF sections.",
  },
  {
    key: "review",
    title: "Review",
    summary: "Check and save or generate.",
  },
];

const normalize_question_rows = (response) => response?.data?.data || [];

const has_template_name = (template_form) =>
  Boolean(String(template_form?.name || "").trim());

const has_selected_entity_types = (template_form) =>
  Boolean((template_form?.entity_type_ids || []).length);

const has_sections = (template_form) =>
  Boolean((template_form?.sections || []).length);

const has_visible_sections = (template_form) => {
  const visible_sections = (template_form?.sections || []).filter(
    (section) => section.is_visible !== false
  );

  return Boolean(visible_sections.length);
};

const build_review_validation_checks = (template_form) => {
  const validation_checks = [
    {
      is_invalid:
        template_form?.save_as_template !== false && !has_template_name(template_form),
      message: "Add a template name before saving.",
    },
    {
      is_invalid: !has_selected_entity_types(template_form),
      message: "Choose at least one entity type.",
    },
    {
      is_invalid: !has_sections(template_form),
      message: "Add at least one document section.",
    },
    {
      is_invalid: !has_visible_sections(template_form),
      message: "Keep at least one section visible in the final PDF.",
    },
  ];

  return validation_checks;
};

const build_step_validation_checks = (step_key, template_form) => {
  const validation_checks = [
    {
      is_invalid:
        step_key === "basics" &&
        template_form?.save_as_template !== false &&
        !has_template_name(template_form),
      message: "Add a template name to continue.",
    },
    {
      is_invalid:
        step_key === "scope" && !has_selected_entity_types(template_form),
      message: "Choose at least one entity type to continue.",
    },
    {
      is_invalid: step_key === "content" && !has_sections(template_form),
      message: "Add at least one section before continuing.",
    },
  ];

  return validation_checks;
};

export const build_formal_question_options = (responses) => {
  const rows = responses.flatMap(normalize_question_rows);
  const by_id = new Map();
  rows.forEach((row) => {
    const question_option = build_question_option(row);
    if (question_option.id) {
      by_id.set(question_option.id, question_option);
    }
  });
  return Array.from(by_id.values())
    .map((question_option) => ({
      ...question_option,
      label: build_question_option_label(question_option),
    }))
    .sort((left_item, right_item) =>
      left_item.label.localeCompare(right_item.label)
    );
};

export const build_system_field_options = (system_fields) =>
  (system_fields || []).map((system_field) => ({
    id: system_field,
    label: build_system_field_label(system_field),
  }));

export const build_review_validation_message = (template_form) => {
  const failed_check = build_review_validation_checks(template_form).find(
    (validation_check) => validation_check.is_invalid
  );
  const validation_message = failed_check?.message || "";

  return validation_message;
};

export const build_step_validation_message = (step_key, template_form) => {
  const failed_check = build_step_validation_checks(step_key, template_form).find(
    (validation_check) => validation_check.is_invalid
  );
  const validation_message = failed_check?.message || "";

  return validation_message;
};

export const build_generation_mode_label = (generation_mode, lookups) => {
  const generation_modes = lookups?.generation_modes || [];
  const selected_mode = generation_modes.find(
    (item) => item.internal_name === generation_mode
  );
  return selected_mode?.display_name || "Manual";
};
